import { reactive } from 'vue'

import {
  MicrofrontendEvents,
  onDispatchToastNotification,
  publish,
} from '@sennder/senn-node-microfrontend-event-bus'
import {
  GetTokenOptions,
  IAction,
  IFeatureFlags,
  IMicrofrontendData,
  IOrg,
  ISennTmsSharedData,
  IUser,
  IUserRole,
  OpsProfile,
  OrgType,
} from '@sennder/senn-node-microfrontend-interfaces'

import { logger } from '@/services/logger/loggers'
import { logout } from '@/store/logoutActions'
import errorsHandler from '@/services/errors-handler'
import { analytics } from '@/services/analyticsProvider'
import { i18n, defaultLanguage } from '@/services/i18n'
import {
  TENANT,
  SHARED_DATA_TYPE,
  ORCAS_URL,
  OPERATIONS_URL,
  SHOW_LANGUAGE_PICKER,
} from '@/common/config'
import { userManagementService } from '@/services/userManagementService'
import { sendErrorInMonitor } from '@/services/monitor'
import { auth } from '@/modules/auth'
import { notify } from './notify'
import router from '@/router'
import { canAccessCharteringPages } from '@/config/user-access'
import { routes } from '@/config/routes'
import { microfrontends } from '@/config/microfrontends'
import startAsyncService from '@/services/retry-service'
// import { identifyUserInLaunchDarkly } from '@/services/launchDarkly'
import { Prettify, Optional } from '@/types/types'
import { featureFlag } from '@/services/featureFlagProvider'

// Constants for audiences
const CHARTERING_SINGLE_AUDIENCE = 'https://api.cloud.sennder.com'

const MFS_WITH_CHARTERING_AUDIENCE_NEEDED = [
  'marketplace-mf-component',
  'chartering',
  'planner-mf-component',
]

// NOTE: This function handles the audience selection based on the following criteria:
// 1. Some modules require a specific audience explicitly provided. If an audience is provided as an argument, it will be returned directly.
// 2. Certain modules inherently need to use the SENN_TMS audience, even if not explicitly specified. These modules are identified and the SENN_TMS_SINGLE_AUDIENCE is returned for them.
// 3. Chartering-related modules require the CHARTERING_SINGLE_AUDIENCE. If the module falls into this category, the function ensures that the chartering audience is selected.
// 4. The function acts as a filter to correctly assign the appropriate audience based on the module and context, ensuring all cases are handled consistently and accurately until all modules are adapted to use a single audience.
const getAudienceBasedOnModule = (): string | undefined => {
  const featureFlags = getStateData().featureFlags

  // Check if the feature flag for chartering MFs is enabled
  if (featureFlags['enable-chartering-mfs-senn-tms']) {
    const currentRoute = router.currentRoute.value
    const routeMeta = Object.values(routes).find(
      (route) => route.name === currentRoute.name
    )
    const module = routeMeta && microfrontends[routeMeta.mf]

    // Check if the current module requires a specific chartering audience
    const isCharteringSpecific =
      module && MFS_WITH_CHARTERING_AUDIENCE_NEEDED.includes(module.npmName)

    // Return the appropriate audience based on the module
    if (isCharteringSpecific) {
      return CHARTERING_SINGLE_AUDIENCE
    }
  }

  // Return undefined if no specific audience is determined
  return undefined
}

// Default options for getting an auth token
const defaultTokenOptions: GetTokenOptions = {
  usePopup: false,
  throwException: false,
}

const getAuthToken = async (options: GetTokenOptions = defaultTokenOptions) => {
  if (!auth.value) {
    throw new Error('[tms-shell - getAuthToken]: Auth module is not loaded')
  }

  const audience = options.audience || getAudienceBasedOnModule()
  const enrichedOptions = { ...options, audience }

  const token = (await auth.value.getAuthToken(enrichedOptions)) || null

  return token
}

const getAuthHeader = async (
  options: GetTokenOptions = defaultTokenOptions
) => {
  const token = await getAuthToken(options)
  return token ? `Bearer ${token}` : null
}

async function getCommonHeaders() {
  if (!auth.value) {
    throw new Error('[tms-shell - getCommonHeaders]: Auth module is not loaded')
  }
  //NOTE: Currently we are using the same audience for all the requests
  return await auth.value.getCommonHeaders(CHARTERING_SINGLE_AUDIENCE)
}

async function getPermissions(actions: IAction[]) {
  if (!auth.value) {
    throw new Error('[tms-shell - getPermissions]: Auth module is not loaded')
  }
  return await auth.value.getPermissions(actions, CHARTERING_SINGLE_AUDIENCE)
}

export async function getOrgTypeAndOrgIdOrFail() {
  if (!auth.value) {
    throw new Error(
      '[tms-shell - getOrgTypeAndOrgIdOrFail]: Auth module is not loaded'
    )
  }
  return await auth.value.getOrgTypeAndOrgIdOrFail()
}
export type SennTmsStoreData = Prettify<
  Optional<ISennTmsSharedData<object>, 'profile'>
> & {
  org?: Org
  user?: IUser
  roles: IUserRole[]
}

type Org = {
  orgID: string
  orgType: OrgType
}

const getEmptyData = (): SennTmsStoreData => {
  return {
    language: defaultLanguage,
    tenant: TENANT,
    type: SHARED_DATA_TYPE,
    featureFlags: {},
    /**
     * TODO: some Octopus micro-frontends expect motership and orcas urls to be passed by the shell because of the multi-tenancy.
     * We can get rid of this and define all the URLs in the micro-frontends themselves.
     */
    env: {
      OPERATIONS_BACKEND_URL: OPERATIONS_URL as string,
      ORCAS_URL: ORCAS_URL as string,
    },
    roles: [],
  }
}

export const store = reactive({
  state: {
    data: getEmptyData(),
    callbacks: {
      getToken: getAuthHeader,
      getAuthToken,
      getAuthHeader,
      getCommonHeaders,
      // @ts-expect-error Untyped in MF Interfaces, only used by internal-auth-mf-v2
      getOrgTypeAndOrgIdOrFail,
      getPermissions,
      syncParentRouter: () => {},
      segmentTrackEvent: () => {},
      onUnauthorized: async () => {
        return logout()
      },
      onUnhandledError: errorsHandler,
    },
    providers: {
      featureFlag: {
        getFeatureFlag: featureFlag.getFeatureFlag,
        watchFeatureFlag: featureFlag.watchFeatureFlag,
      },
      monitoring: {
        sendError: sendErrorInMonitor,
      },
      logger,
      notifications: {
        error: (message: string) => notify(message, 'error'),
        success: (message: string) => notify(message, 'success'),
      },
      analytics,
    },
  } satisfies IMicrofrontendData<SennTmsStoreData>,
})

export const getStateData = (): SennTmsStoreData => {
  return store.state.data
}

// Loosely typed and dangerous, only use this function from dev-panel
export const DEV_setStateData = (data: object) => {
  // @ts-expect-error
  store.state.data = data
}

export const getUserProfile = () => {
  if (!store.state.data.profile) {
    throw new Error(
      '[tms-shell - getUserProfile] state.data.profile is not initialized'
    )
  }

  return store.state.data.profile
}

export const getStateProviders = () => {
  return store.state.providers
}

export const getStateCallbacks = () => {
  return store.state.callbacks
}

export const loadState = async () => {
  let user: IUser
  let org: Org | undefined
  let opsProfile: OpsProfile
  try {
    ;[org, { opsProfile, user }] = await Promise.all([
      fetchOrg(),
      userManagementService.getUserAndProfile(),
    ])
    setStoreLanguage(SHOW_LANGUAGE_PICKER ? user.language : 'en')
  } catch (error) {
    logger.error(
      '[tms-shell - userManagementService]: User data can not be loaded',
      {
        error,
      }
    )

    publish<onDispatchToastNotification>({
      name: MicrofrontendEvents.onDispatchToastNotification,
      data: {
        message: i18n.global.t('toast.error.message'),
        category: 'error',
        title: i18n.global.t('toast.error.title'),
      },
    })
    await logout()
    return false
  }

  const [userRoles] = await Promise.all([
    fetchUserRoles(opsProfile.id, org),
    startAsyncService(
      'LaunchDarkly',
      () =>
        featureFlag.identify(opsProfile.id, {
          email: opsProfile.email,
          name: opsProfile.name,
        }),
      {},
      0
    ),
  ])

  store.state.data = {
    ...store.state.data,
    user,
    org,
    profile: opsProfile,
    roles: userRoles || [],
  }
  return true
}

const fetchUserRoles = async (id: string, org?: IOrg) => {
  if (!id) {
    return
  }

  try {
    const uMRoles = await userManagementService.getUserRoles(id)
    const userRoles = [...uMRoles]

    if (canAccessCharteringPages(org)) {
      if (!auth.value) {
        logger.error(
          '[tms-shell - set user chartering roles]: Auth module is not loaded',
          {}
        )
      } else {
        const charteringRole = await auth.value.getCharteringUserRole?.(id)

        if (charteringRole) {
          const baseRole: IUserRole = {
            id: charteringRole.userRoleInfo?.uuid,
            name: charteringRole.userRoleInfo?.role,
            description: charteringRole.userRoleInfo?.name,
          }

          if (baseRole.name) {
            userRoles.push(baseRole)
          }

          if (charteringRole.charteringOfficeName) {
            userRoles.push({
              ...baseRole,
              name: charteringRole.charteringOfficeName,
            })
          }
        }
      }
    }

    return userRoles
  } catch (error) {
    logger.error(
      '[tms-shell - userManagementService]: User roles can not be load',
      { error }
    )
  }
}

export const fetchOrg = async (): Promise<Org | undefined> => {
  try {
    const { orgId, orgType } = await getOrgTypeAndOrgIdOrFail()
    if (orgType && orgId) {
      return { orgID: orgId, orgType: orgType as OrgType }
    }
  } catch (error) {
    logger.error('[tms-shell - fetchOrg]: Error while getting org', {
      error,
    })
  }
}

export const setStoreLanguage = (language: string) => {
  store.state.data.language = language
}

export const setStoreLegacyFlags = (flags: IFeatureFlags) => {
  store.state.data.featureFlags = {
    ...store.state.data.featureFlags,
    ...flags,
  }
}

export const setStoreData = (data: SennTmsStoreData) => {
  store.state.data = data
}

export const clearState = () => {
  store.state.data = getEmptyData()
}
